import Vue, { PropType } from 'vue';
import { noop as _noop, isEmpty as _isEmpty } from 'lodash';
import { ValidationGroups, ValidationProperties } from 'vue/types/vue';
import { Validation } from 'vuelidate';
import { VueConstructor } from 'vue/types/umd';
import { appState } from '@/services/form/appState';
import { Form } from '@/types/forms/form';
import { FormDto } from '@/services/form/dto';
import { modelService } from '@/services/form/modelService';
import { authService } from '@/services/authService';
import { FormContent } from '@/api/interfaces/content/form/formContent';

// eslint-disable-next-line @typescript-eslint/comma-dangle
interface FormStepComponent<
    TModel,
    TContent,
    TConfiguration,
    TFormType extends Form = Form
> extends Vue {
    readonly content: TContent;
    readonly formContent: FormContent;
    readonly configuration: TConfiguration;
    readonly model: TModel;
    readonly formModel: TFormType;
    readonly validations:
        | (Validation & ValidationGroups & ValidationProperties<unknown>)
        | null;
    readonly rules: (component: this) => void;
    readonly showSummary: boolean;
}

// eslint-disable-next-line @typescript-eslint/comma-dangle
export function getFormStepMixin<
    TModel,
    TContent,
    TConfiguration,
    TFormType extends Form = Form
>(): VueConstructor<
    FormStepComponent<TModel, TContent, TConfiguration, TFormType>
> {
    return Vue.extend({
        props: {
            content: {
                type: Object as PropType<TContent>,
                default: () => ({} as TContent),
            },
            formContent: {
                type: Object as PropType<FormContent>,
                default: () => ({} as FormContent),
            },
            configuration: {
                type: Object as PropType<TConfiguration>,
                default: () => ({} as TConfiguration),
            },
            identifier: {
                type: String,
                default: null,
            },
            debugDto: {
                type: Object as PropType<FormDto>,
                default: () => ({}),
            },
            model: {
                type: Object as PropType<TModel>,
                default: () => ({} as TModel),
            },
            formModel: {
                type: Object as PropType<TFormType>,
                default: () => ({} as TFormType),
            },
            validations: {
                type: Object as PropType<
                    | (Validation &
                          ValidationGroups &
                          ValidationProperties<TModel>)
                    | null
                >,
                default: null,
            },
            rules: {
                type: Function as PropType<
                    (
                        component: FormStepComponent<
                            TModel,
                            TContent,
                            TConfiguration
                        >,
                        cb: () => void,
                    ) => void
                >,
                default: () => {
                    return _noop;
                },
            },
            showSummary: {
                type: Boolean,
                default: false,
            },
        },
        data() {
            return {
                disableWatch: false,
                appState,
            };
        },
        computed: {
            isDebugUser(): boolean {
                return (
                    authService.isAuthenticated &&
                    authService.hasRoles('DEA Debug')
                );
            },
            showDebugFillButton(): boolean {
                return this.isDebugUser && !_isEmpty(this.debugDto);
            },
            showDebugResetButton(): boolean {
                const enabledSteps = this.formModel?.enabledSteps;
                return (
                    this.isDebugUser &&
                    !this.formModel?.editStep &&
                    !!enabledSteps &&
                    enabledSteps.indexOf(this.identifier) > 0
                );
            },
            allValidationsAreValid(): boolean {
                return !this.validations?.$invalid;
            },
        },
        watch: {
            model: {
                handler() {
                    if (!this.rules || this.disableWatch) {
                        return;
                    }
                    this.disableWatch = true;
                    this.rules(this, () => {
                        this.disableWatch = false;
                    });
                },
                deep: true,
                immediate: true,
            },
        },
        methods: {
            isFormValid(): boolean {
                this.validations?.$touch();
                return this.allValidationsAreValid;
            },
            async debugFillAdditionalData(): Promise<void> {
                return;
            },
            async debugFillSubmit(): Promise<void> {
                appState.debugFillInProgress = true;
                this.disableWatch = true;
                try {
                    await this.$nextTick();
                    modelService.loadFormIntoModel(
                        this.debugDto,
                        `application.${this.identifier}`,
                    );
                    await this.$nextTick();
                    await this.rules(this, _noop);
                    await this.$nextTick();
                    await this.debugFillAdditionalData();
                    await this.$nextTick();
                } finally {
                    this.disableWatch = false;
                    appState.debugFillInProgress = false;
                }
                return this.validateAndSubmit();
            },
            debugResetStep(): void {
                this.$emit('debug-reset-step', this.identifier);
            },
            async validateAndSubmit(): Promise<void> {
                if (this.isFormValid()) {
                    this.$emit('submit');
                } else {
                    await this.$nextTick();
                    this.$emit('validation-failed');
                }
            },
        },
    });
}
